/** @format */

import axios from "axios";
import { API_BASE_URL } from "../../axiosConfig/apiUrl";
import { IBusiness, BusinessRole } from "../../utility/types/collectTypes";

// ─────────────────────────────────────────────────────────────────────────
// Collect BUSINESS auth API surface.
//
// Separate from the circle auth (`auth/authenticate.ts`): authenticates a
// dedicated `BusinessUser` against the same Cognito pool, using business-scoped
// `b_*` tokens. Tokens are persisted to localStorage so the global axios
// interceptor attaches them to `/collect/*` requests (mobile-Safari ITP safe).
// ─────────────────────────────────────────────────────────────────────────

export interface IBusinessUserProfile {
    _id: string;
    email: string;
    firstName: string;
    lastName: string;
    status: string;
}

export interface IBusinessAuthMembership {
    membershipId?: string;
    businessId: string;
    role: BusinessRole;
    business?: IBusiness;
}

export interface IBusinessAuthTokens {
    accessToken: string;
    idToken: string;
    refreshToken: string;
}

export interface IBusinessLoginResult {
    businessUser: IBusinessUserProfile;
    memberships: IBusinessAuthMembership[];
    tokens: IBusinessAuthTokens;
}

export interface IRegisterAdminPayload {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    phone?: string;
    businessName: string;
    timezone?: string;
    country: string;
}

export interface ISupportedCountry {
    code: string;
    name: string;
    currency: string;
    currencySymbol: string;
}

export interface IRegisterTeamPayload {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    phone?: string;
    businessId: string;
}

export interface IRegisterResult {
    businessUserId: string;
    businessId?: string;
    email: string;
    needsVerification: boolean;
    resent?: boolean;
}

function pickError(error: any, fallback: string): string {
    return (
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        fallback
    );
}

const B_ACCESS = "b_accessToken";
const B_ID = "b_idToken";
const B_REFRESH = "b_refreshToken";
const B_USER = "b_user";

/** Persist business tokens + profile so the interceptor can attach them. */
export function persistBusinessSession(
    tokens: IBusinessAuthTokens,
    businessUser: IBusinessUserProfile,
): void {
    localStorage.setItem(B_ACCESS, tokens.accessToken);
    localStorage.setItem(B_ID, tokens.idToken);
    localStorage.setItem(B_REFRESH, tokens.refreshToken);
    localStorage.setItem(B_USER, JSON.stringify(businessUser));
}

/** Remove all business session state from localStorage. */
export function clearBusinessSession(): void {
    localStorage.removeItem(B_ACCESS);
    localStorage.removeItem(B_ID);
    localStorage.removeItem(B_REFRESH);
    localStorage.removeItem(B_USER);
}

/** Read the cached business profile (if a session exists). */
export function readCachedBusinessUser(): IBusinessUserProfile | null {
    try {
        const raw = localStorage.getItem(B_USER);
        return raw ? (JSON.parse(raw) as IBusinessUserProfile) : null;
    } catch {
        return null;
    }
}

export function hasBusinessSession(): boolean {
    return Boolean(localStorage.getItem(B_ACCESS));
}

export const submitRegisterBusinessAdmin = async (
    payload: IRegisterAdminPayload,
): Promise<IRegisterResult> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/auth/register`,
            payload,
            { withCredentials: true },
        );
        return res.data.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to create account."));
    }
};

/** Public: list the billing countries the signup form may offer. */
export const fetchSupportedCountries = async (): Promise<
    ISupportedCountry[]
> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/supported-countries`,
        );
        return res.data.data as ISupportedCountry[];
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load countries."));
    }
};

export const submitRegisterTeamMember = async (
    payload: IRegisterTeamPayload,
): Promise<IRegisterResult> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/auth/team-register`,
            payload,
            { withCredentials: true },
        );
        return res.data.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to create account."));
    }
};

export const submitVerifyBusinessUser = async (
    email: string,
    code: string,
): Promise<{ verified: boolean; email: string; redirectTo: string }> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/auth/verify`,
            { email, code },
            { withCredentials: true },
        );
        return res.data.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Verification failed."));
    }
};

export const submitResendBusinessCode = async (
    email: string,
): Promise<{ resent: boolean }> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/auth/resend-code`,
            { email },
            { withCredentials: true },
        );
        return res.data.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to resend code."));
    }
};

export const submitForgotBusinessPassword = async (
    email: string,
): Promise<{ message: string }> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/auth/forgot-password`,
            { email },
            { withCredentials: true },
        );
        return { message: res.data?.message || "" };
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to start password reset."));
    }
};

export const submitResetBusinessPassword = async (
    email: string,
    code: string,
    newPassword: string,
): Promise<{ reset: boolean; email: string; redirectTo: string }> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/auth/reset-password`,
            { email, code, newPassword },
            { withCredentials: true },
        );
        return res.data.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to reset password."));
    }
};

export const submitLoginBusinessUser = async (
    identifier: string,
    password: string,
): Promise<IBusinessLoginResult> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/auth/login`,
            { identifier, password },
            { withCredentials: true },
        );
        const data = res.data.data as IBusinessLoginResult;
        persistBusinessSession(data.tokens, data.businessUser);
        return data;
    } catch (e: any) {
        throw new Error(pickError(e, "Sign in failed."));
    }
};

export const submitLogoutBusinessUser = async (): Promise<void> => {
    try {
        await axios.post(
            `${API_BASE_URL}/collect/auth/logout`,
            {},
            { withCredentials: true },
        );
    } catch {
        // Sign-out is best-effort; we always clear local state below.
    } finally {
        clearBusinessSession();
    }
};

// ─────────────────────────────────────────────────────────────────────────
// My Account — self-service profile + password for the signed-in BusinessUser.
// Auth (business Bearer token) is injected by the global axios interceptor for
// any `/collect/*` request; the profile read additionally needs the active
// workspace via the `x-business-id` header (role is per-workspace).
// ─────────────────────────────────────────────────────────────────────────

export interface IMyAccount {
    _id: string;
    email: string;
    firstName: string;
    lastName: string;
    phone?: string;
    status: string;
    role?: BusinessRole;
    roleName: string;
    roleLabel: string;
}

/** Patch the cached business profile name so the rest of the UI stays in sync. */
function patchCachedBusinessUserName(
    firstName: string,
    lastName: string,
): void {
    const current = readCachedBusinessUser();
    if (!current) return;
    persistBusinessUserProfileOnly({
        ...current,
        firstName,
        lastName,
    });
}

/** Persist ONLY the profile object (used when tokens are untouched). */
function persistBusinessUserProfileOnly(
    businessUser: IBusinessUserProfile,
): void {
    localStorage.setItem(B_USER, JSON.stringify(businessUser));
}

export const fetchMyAccountApi = async (
    businessId: string,
): Promise<IMyAccount> => {
    try {
        const res = await axios.get(`${API_BASE_URL}/collect/account/me`, {
            withCredentials: true,
            headers: { "x-business-id": businessId },
        });
        return res.data.data as IMyAccount;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load your account."));
    }
};

export const updateMyProfileApi = async (input: {
    firstName: string;
    lastName: string;
}): Promise<IBusinessUserProfile> => {
    try {
        const res = await axios.put(
            `${API_BASE_URL}/collect/account/profile`,
            { firstName: input.firstName, lastName: input.lastName },
            { withCredentials: true },
        );
        const updated = res.data.data as IBusinessUserProfile;
        patchCachedBusinessUserName(updated.firstName, updated.lastName);
        return updated;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to update your profile."));
    }
};

export const changeMyPasswordApi = async (input: {
    currentPassword: string;
    newPassword: string;
}): Promise<void> => {
    try {
        await axios.put(
            `${API_BASE_URL}/collect/account/password`,
            {
                currentPassword: input.currentPassword,
                newPassword: input.newPassword,
            },
            { withCredentials: true },
        );
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to update your password."));
    }
};
